/* eslint-disable react/no-array-index-key */
import React, {
  lazy,
  Suspense,
  Fragment
} from 'react';
import {
  Switch,
  Redirect,
  Route
} from 'react-router-dom';

import MainLayout from 'src/layouts/MainLayout';
import NavigableLayout from 'src/layouts/NavigableLayout';
import LoadingScreen from 'src/components/LoadingScreen';

import ProtectedPage from 'src/components/AccessControl/ProtectedPage';
import {
  CreateClientsRole,
  CreatePhotoRole,
  CreateLookupsRole,
  UpdateClientRole,
  UpdateContractRole,
  UpdatePropertyRole,
  DeletePhotoRole,
  ReadAccessRole,
} from 'src/components/AccessControl';

const routesConfig = [
  {
    exact: true,
    path: '/',
    component: () => <Redirect to="/home" />
  },
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/pages/404Page/Error404Page'))
  },
  {
    path: '/home',
    // guard: AuthGuard,
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/home',
        component: lazy(() => import('src/pages/DashboardPage'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/clients',
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/clients',
        component: lazy(() => import('src/pages/Client/Clients')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        exact: true,
        path: '/clients/create',
        component: lazy(() => import('src/pages/Client/Create')),
        accessControl: { accessRoles: [ReadAccessRole, CreateClientsRole] },
      },
      {
        exact: true,
        path: '/clients/:clientId',
        component: lazy(() => import('src/pages/Client/Details')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        exact: true,
        path: '/clients/:clientId/edit',
        component: lazy(() => import('src/pages/Client/Edit')),
        accessControl: { accessRoles: [ReadAccessRole, UpdateClientRole] },
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/properties',
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/properties',
        component: lazy(() => import('src/pages/Property/Properties')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        exact: true,
        path: '/properties/:propertyId',
        component: lazy(() => import('src/pages/Property/Details')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        exact: true,
        path: '/properties/:propertyId/edit',
        component: lazy(() => import('src/pages/Property/Edit')),
        accessControl: { accessRoles: [ReadAccessRole, UpdatePropertyRole] },
      },
      {
        exact: true,
        path: '/properties/:propertyId/photos/create',
        component: lazy(() => import('src/pages/Photo/Create')),
        accessControl: { accessRoles: [ReadAccessRole, UpdatePropertyRole, CreatePhotoRole] },
      },
      {
        exact: true,
        path: '/properties/:propertyId/photos/:photoId/edit',
        component: lazy(() => import('src/pages/Photo/Edit')),
        accessControl: { accessRoles: [ReadAccessRole, UpdatePropertyRole, DeletePhotoRole] },
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/contracts',
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/contracts',
        component: lazy(() => import('src/pages/Contract/Contracts')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        exact: true,
        path: '/contracts/:contractId',
        component: lazy(() => import('src/pages/Contract/Details')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        exact: true,
        path: '/contracts/:contractId/edit',
        component: lazy(() => import('src/pages/Contract/Edit')),
        accessControl: { accessRoles: [ReadAccessRole, UpdateContractRole] },
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/feeds',
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/feeds',
        component: lazy(() => import('src/pages/FeedUploadPage')),
        accessControl: { accessRoles: [ReadAccessRole] },
      },
      {
        // Update when feed-specific routes are added
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/monthly-close',
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/monthly-close',
        component: lazy(() => import('src/pages/MonthlyClose'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/lookups',
    layout: NavigableLayout,
    routes: [
      {
        exact: true,
        path: '/lookups',
        component: lazy(() => import('src/pages/LookupsPage')),
        accessControl: { accessRoles: [ReadAccessRole, CreateLookupsRole] }
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

const renderRoutes = (routes) => (routes ? (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const AuthGuard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        const AccessGuard = route.accessControl ? ProtectedPage : Fragment;
        const accessProps = route.accessControl || {};

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <AuthGuard>
                <AccessGuard {...accessProps}>
                  <Layout>
                    {route.routes
                      ? renderRoutes(route.routes)
                      : <Component {...props} />}
                  </Layout>
                </AccessGuard>
              </AuthGuard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
) : null);

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
